import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { PieChartOutlined } from '@ant-design/icons';
import { List, ListItem } from '@mui/material';
import { AuditLogIcon, AuthIcon, ClientsIcon, CommsIcon, ImportsIcon, IntegrationsIcon, LocaleIcon, MetadataIcon, OverviewIcon, PayrollIcon, PolicyIcon, SchoolsIcon, UsersIcon, } from '@src/components/Icons';
import { FieldOfStudyIcon } from '@src/components/Icons/FieldOfStudyIcon';
import { useAuth } from '@src/hooks/useAuth';
import { useClientIdParam } from '@src/hooks/useClientIdParam';
import { useClients } from '@src/hooks/useClients';
import { filterByRoles } from '@src/utilities/permissions';
import { ActivityIcon, UserSquareIcon } from 'lucide-react';
import { classNames, DropdownSelect, Role, useLocale } from 'pe-core-frontend';
import { useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styles from './ClientsMenu.module.scss';
const MenuItems = [
    {
        title: 'Dashboard',
        children: [
            {
                title: 'Overview',
                path: 'dashboard',
                icon: _jsx(OverviewIcon, { fontSize: 'small' }),
            },
            {
                title: 'Activity',
                path: 'activity',
                icon: _jsx(ActivityIcon, { fontSize: 'small' }),
            },
            {
                title: 'Reports',
                path: 'reports',
                icon: _jsx(PieChartOutlined, { style: { fontSize: '20px', fontWeight: 'bold' } }),
                roles: [Role.SYS_ADMIN],
            },
        ],
        isVisible: (client) => !client.isParent,
    },
    {
        title: 'Operations',
        children: [
            {
                title: 'Imports',
                path: 'imports',
                icon: _jsx(ImportsIcon, { fontSize: 'small' }),
                roles: [Role.SYS_ADMIN],
            },
            {
                title: 'Payments',
                path: 'payroll',
                icon: _jsx(PayrollIcon, { fontSize: 'small' }),
                roles: [Role.SYS_ADMIN],
            },
            {
                title: 'Employees',
                path: 'employees',
                icon: _jsx(UserSquareIcon, {}),
                isVisible: (client) => !client.isParent,
            },
            {
                title: 'Users',
                path: 'users',
                icon: _jsx(UsersIcon, {}),
                isVisible: (client) => !client.isParent,
            },
            {
                title: 'Audit Log',
                path: 'audit_logs',
                icon: _jsx(AuditLogIcon, {}),
                roles: [Role.SYS_ADMIN],
                isVisible: (client) => !client.isParent,
            },
        ],
    },
    {
        title: 'Configurations',
        roles: [Role.SYS_ADMIN],
        children: [
            {
                title: 'Metadata',
                path: 'metadata',
                icon: _jsx(MetadataIcon, { fontSize: 'small' }),
            },
            {
                title: 'Policy',
                path: 'policies',
                icon: _jsx(PolicyIcon, { fontSize: 'small' }),
                isVisible: (client) => !client.isParent,
            },
            {
                title: 'Content',
                path: 'comms',
                icon: _jsx(CommsIcon, { fontSize: 'small' }),
                isVisible: (client) => !client.isParent,
            },
            {
                title: 'Authentication',
                path: 'auth',
                icon: _jsx(AuthIcon, { fontSize: 'small' }),
                isVisible: (client) => !client.isParent,
            },
            {
                title: 'Locales',
                path: 'locale',
                icon: _jsx(LocaleIcon, { fontSize: 'small' }),
                isVisible: (client) => !client.isParent,
            },
            {
                title: 'Integrations',
                path: 'integrations',
                icon: _jsx(IntegrationsIcon, { fontSize: 'small' }),
            },
            {
                title: 'Fields of study',
                path: 'fields-of-study',
                icon: _jsx(FieldOfStudyIcon, { fontSize: 'small' }),
                isVisible: (client) => !client.isParent,
            },
            {
                title: 'Program Levels',
                path: 'program-levels',
                icon: _jsx(SchoolsIcon, { fontSize: 'small' }),
                isVisible: (client) => !client.isParent,
            },
            {
                title: 'Child Clients',
                path: 'child-clients',
                icon: _jsx(ClientsIcon, { fontSize: 'small' }),
                isVisible: (client) => client.isParent ?? false,
            },
        ],
    },
];
export const ClientsMenu = () => {
    const { t } = useLocale();
    const { isClientAdmin, currentUser } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const clientId = useClientIdParam();
    const { clients } = useClients({ initialPageSize: 100 }); // we need to show all clients in the list
    const basePath = `/admin/clients/${clientId}`;
    const selectedClient = useMemo(() => clients.find(c => c.id === Number(clientId)), [clients, clientId]);
    const clientOptions = useMemo(() => {
        return clients.map(client => ({
            label: client.name,
            value: client.id,
        }));
    }, [clients]);
    const handleClientChange = (e) => {
        const newPath = location.pathname.replace(basePath, `/admin/clients/${e.target.value}`);
        navigate(newPath);
    };
    const isItemVisible = (item) => {
        if (!filterByRoles(item, currentUser?.roleNames)) {
            return false;
        }
        return !selectedClient || !item.isVisible || item.isVisible?.(selectedClient);
    };
    return (_jsxs(List, { className: styles.container, children: [_jsx("div", { className: 'flex flexJustify--end' }), !isClientAdmin && (_jsx(DropdownSelect, { className: 'w-full', btnClass: 'w-full', btnStyle: {
                    color: 'var(--admin-secondary-color, #170b01)',
                    padding: '12px',
                    fontWeight: '400',
                }, label: t('Client'), options: clientOptions, value: Number(clientId || 0), onChange: handleClientChange, color: 'primary', variant: 'contained', isClearable: false })), MenuItems.filter(isItemVisible).map(item => (_jsxs(ListItem, { className: classNames(styles.menuItem, item.children && item.children.length > 0 ? styles.hasChildren : ''), children: [_jsx("span", { children: t(item.title) }), item.children && (_jsx(List, { className: styles.subMenu, children: item.children.filter(isItemVisible).map(child => (_jsx(ListItem, { className: classNames(styles.subMenuItem, location.pathname.startsWith(`${basePath}/${child.path}`) ? styles.selected : ''), children: _jsxs(Link, { to: `${basePath}/${child.path}`, children: [child.icon, t(child.title)] }) }, child.title))) }))] }, item.title)))] }));
};
